import type {
  IOpeningHourRow,
  ISetupOrdersDto,
  TDeliveryRadiusKm,
  TSetupOrdersMode,
} from '@/interfaces/IStoreSetup';
import { rowsToWeek } from './opening-hours';
import type { TWeekHours } from './opening-hours';
import { SETUP_PAYMENTS } from './orders-setup';
import type {
  IOrdersForm,
  TOrderType,
  TOrdersBlocker,
  TPaymentChipId,
} from './orders-setup';

/**
 * Pure rules of the «Pedidos» screen (plan §4.8): what each control changes
 * in the form, the reach control's segments, the bottom-bar helper, the
 * `orders_configured` analytics params and the delivery map's bounds.
 */

export type TOrdersFormPatch = Partial<IOrdersForm>;

export interface IOrderTypeOption {
  type: TOrderType;
  titleKey: string;
  textKey: string;
  icon: string;
}

/** The three order-type cards, in display order. */
export const ORDER_TYPE_OPTIONS: readonly IOrderTypeOption[] = [
  {
    type: 'table',
    titleKey: 'storeSetup.orders.table',
    textKey: 'storeSetup.orders.tableText',
    icon: 'qrcode',
  },
  {
    type: 'pickup',
    titleKey: 'storeSetup.orders.pickup',
    textKey: 'storeSetup.orders.pickupText',
    icon: 'storefront-outline',
  },
  {
    type: 'delivery',
    titleKey: 'storeSetup.orders.delivery',
    textKey: 'storeSetup.orders.deliveryText',
    icon: 'moped-outline',
  },
];

/** The form change that turns one order type on or off (the type names are the form fields). */
function orderTypePatch(type: TOrderType, on: boolean): TOrdersFormPatch {
  switch (type) {
    case 'table':
      return { table: on };
    case 'pickup':
      return { pickup: on };
    case 'delivery':
      return { delivery: on };
  }
}

/** A card shows as chosen only while orders are on (menu-only dims all three). */
export function isOrderTypeOn(form: IOrdersForm, type: TOrderType): boolean {
  return !form.menuOnly && form[type];
}

/**
 * A tap on an order-type card: it toggles the type; while «Por ahora, solo
 * mostrar el menú» is chosen, the tap clears it and turns that type on.
 */
export function toggleOrderType(
  form: IOrdersForm,
  type: TOrderType
): TOrdersFormPatch {
  if (form.menuOnly) return { menuOnly: false, ...orderTypePatch(type, true) };
  return orderTypePatch(type, !form[type]);
}

/** «Por ahora, solo mostrar el menú» toggles; the order types keep their state underneath. */
export function toggleMenuOnly(form: IOrdersForm): TOrdersFormPatch {
  return { menuOnly: !form.menuOnly };
}

/** Typing an amount chooses the amount (the chips give way). */
export function feeTextPatch(text: string): TOrdersFormPatch {
  return { feeText: text, feeMode: 'amount' };
}

/** «Gratis» / «A definir»: a tap chooses it; a second tap returns to the amount. */
export function toggleFeeChip(
  form: IOrdersForm,
  mode: 'free' | 'tbd'
): TOrdersFormPatch {
  return { feeMode: form.feeMode === mode ? 'amount' : mode };
}

export function isPaymentChipOn(
  form: IOrdersForm,
  id: TPaymentChipId
): boolean {
  return id === 'mercadoPago' ? form.mercadoPago : form.payments.includes(id);
}

/** A payment chip toggles; payments keep the chips' order. */
export function togglePayment(
  form: IOrdersForm,
  id: TPaymentChipId
): TOrdersFormPatch {
  if (id === 'mercadoPago') return { mercadoPago: !form.mercadoPago };
  const on = !form.payments.includes(id);
  return {
    payments: SETUP_PAYMENTS.filter((payment) =>
      payment === id ? on : form.payments.includes(payment)
    ),
  };
}

/** Where orders arrive; WhatsApp orders cannot be paid with Mercado Pago, so it is deselected. */
export function setOrdersMode(mode: TSetupOrdersMode): TOrdersFormPatch {
  return mode === 'wa' ? { mode, mercadoPago: false } : { mode };
}

// ---------------------------------------------------------------------------
// Reach («¿Hasta dónde llega?»)
// ---------------------------------------------------------------------------

/** `PosUiSegmented` ids ('0' = «Sin límite»); '' while nothing is chosen. */
export type TRadiusSegmentId = '2' | '5' | '10' | '0';

export interface IRadiusSegment {
  id: TRadiusSegmentId;
  km: TDeliveryRadiusKm;
}

export const RADIUS_SEGMENTS: readonly IRadiusSegment[] = [
  { id: '2', km: 2 },
  { id: '5', km: 5 },
  { id: '10', km: 10 },
  { id: '0', km: 0 },
];

export function radiusSegmentValue(radius: TDeliveryRadiusKm | null): string {
  return RADIUS_SEGMENTS.find((segment) => segment.km === radius)?.id ?? '';
}

export function radiusFromSegment(id: string): TDeliveryRadiusKm | null {
  return RADIUS_SEGMENTS.find((segment) => segment.id === id)?.km ?? null;
}

const KM_PER_DEGREE_LAT = 111.32;

/**
 * South-west and north-east corners of a circle of `km` around `center`
 * (Leaflet `LatLngBoundsExpression` order), to fit the delivery map.
 */
export function circleBounds(
  center: { lat: number; lng: number },
  km: number
): [[number, number], [number, number]] {
  const latDelta = km / KM_PER_DEGREE_LAT;
  const cos = Math.cos((center.lat * Math.PI) / 180);
  const lngDelta = km / (KM_PER_DEGREE_LAT * Math.max(cos, 0.01));
  return [
    [center.lat - latDelta, center.lng - lngDelta],
    [center.lat + latDelta, center.lng + lngDelta],
  ];
}

// ---------------------------------------------------------------------------
// Bottom bar and analytics
// ---------------------------------------------------------------------------

const BLOCKER_HELPER_KEYS: Readonly<Record<TOrdersBlocker, string>> = {
  types: 'storeSetup.orders.helperChooseType',
  fee: 'storeSetup.orders.helperChooseDelivery',
  payments: 'storeSetup.orders.helperChoosePay',
  whatsapp: 'storeSetup.orders.whatsappInvalid',
};

/**
 * The first unmet rule; with nothing missing, what «Configurar después»
 * leaves, only while that button is in the bar (`hasLater`: not from the
 * checklist, not offline), else no helper (review round 5, m1).
 */
export function ordersHelperKey(
  blocker: TOrdersBlocker | null,
  hasLater: boolean
): string | null {
  if (blocker !== null) return BLOCKER_HELPER_KEYS[blocker];
  return hasLater ? 'storeSetup.orders.helperLater' : null;
}

/** GA `orders_configured` params (§4.13); lists are comma-joined strings. */
export interface IOrdersConfiguredEvent {
  types: string;
  feeMode: string;
  radius: number | null;
  payments: string;
  mode: TSetupOrdersMode;
  menuOnly: boolean;
  checklist: boolean;
}

/** The params of what was saved (the `PUT …/orders` body), so they match the branch. */
export function ordersConfiguredEvent(
  dto: ISetupOrdersDto,
  checklist: boolean
): IOrdersConfiguredEvent {
  const types: TOrderType[] = [];
  if (!dto.menuOnly) {
    if (dto.tableOrders) types.push('table');
    if (dto.takeAway) types.push('pickup');
    if (dto.delivery) types.push('delivery');
  }
  const payments: string[] = dto.menuOnly ? [] : [...dto.payments];
  if (dto.wantsMercadoPago) payments.push('mercadoPago');
  return {
    types: types.join(','),
    feeMode: dto.deliveryFeeMode ?? 'none',
    radius: dto.deliveryRadiusKm ?? null,
    payments: payments.join(','),
    mode: dto.ordersMode,
    menuOnly: dto.menuOnly,
    checklist,
  };
}

// ---------------------------------------------------------------------------
// After the save
// ---------------------------------------------------------------------------

/**
 * Merges the saved branch into every loaded copy of it (the store's list,
 * the legacy lists, the selected branch), never replacing them: the answer
 * has no `hourRanges`, `location` or `token`, which other screens read.
 * Each distinct object is merged once; other branches are left alone.
 */
export function mergeBranchUpdate<T extends { id: number }>(
  copies: readonly (T | null | undefined)[],
  update: Partial<T> & { id: number }
): number {
  const targets = new Set<T>();
  for (const copy of copies)
    if (copy && copy.id === update.id) targets.add(copy);
  targets.forEach((target) => Object.assign(target, update));
  return targets.size;
}

// ---------------------------------------------------------------------------
// Branch hours (the preview's «Hoy {hours}»)
// ---------------------------------------------------------------------------

const TIME_PREFIX = /^(\d{2}:\d{2})/;

/** `HH:MM` of a TIME value (`12:00:00` → `12:00`). */
function hourMinute(time: string): string {
  const match = TIME_PREFIX.exec(time.trim());
  return match ? match[1] : time.trim();
}

/** The branch's `hour-ranges` rows as a week; null without rows (no hours). */
export function branchWeek(
  rows: readonly IOpeningHourRow[] | null | undefined
): TWeekHours | null {
  if (!rows || rows.length === 0) return null;
  return rowsToWeek(
    rows.map((row) => ({
      day: row.day,
      from: hourMinute(row.from),
      to: hourMinute(row.to),
    }))
  );
}
